import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { InstrumentPicker } from "./InstrumentPicker";
import { ConfirmSheet } from "./ConfirmSheet";

const Label = ({ children }) => (
  <label className="block text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-400 mb-1.5">
    {children}
  </label>
);

const inputCls =
  "w-full rounded-sm bg-zinc-900/70 border border-zinc-700 px-3 py-2.5 font-mono text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus:outline-none transition-colors duration-200";

/* Single ticket with a Market / Limit toggle — the only place orders are placed. */
export const TradeForm = ({ symbols, settings, symbol: symbolProp, tick, onSymbol, onPlaced }) => {
  const [orderType, setOrderType] = useState("market");
  const [ownSymbol, setOwnSymbol] = useState("");
  const [side, setSide] = useState("buy");
  const [limitPrice, setLimitPrice] = useState("");
  const [sl, setSl] = useState("");
  const [slMode, setSlMode] = useState("price");
  const [slPoints, setSlPoints] = useState("");
  const [tp1, setTp1] = useState("");
  const [tp2, setTp2] = useState("");
  const [moveBe, setMoveBe] = useState(null);   // null = follow the desk rule
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [sheetResult, setSheetResult] = useState(null);

  const symbol = symbolProp !== undefined ? symbolProp : ownSymbol;
  const setSymbol = (s) => (onSymbol ? onSymbol(s) : setOwnSymbol(s));
  const isLimit = orderType === "limit";
  const isPoints = slMode === "points";

  /* Points mode: raw price points away from the reference price — the order price on a limit
     order, the live bid/ask on a market order. */
  const digits = preview?.digits ?? tick?.digits ?? 5;
  const refPrice = isLimit
    ? parseFloat(limitPrice) || null
    : (side === "buy" ? parseFloat(tick?.ask) : parseFloat(tick?.bid)) || null;
  const pointsSl = (() => {
    const pts = parseFloat(slPoints);
    if (!isPoints || !pts || !refPrice) return null;
    const raw = side === "buy" ? refPrice - pts : refPrice + pts;
    return Number(raw.toFixed(digits));
  })();
  const slValue = isPoints ? pointsSl : parseFloat(sl) || null;
  const refForPreview = isLimit ? refPrice : null;

  useEffect(() => {
    if (!symbol || !slValue) {
      setPreview(null);
      setPreviewErr("");
      return;
    }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post("/trade/preview", {
          symbol,
          side,
          stop_loss: slValue,
          ref_price: refForPreview,
        });
        setPreview(data);
        setPreviewErr(data.error || "");
      } catch (e) {
        setPreview(null);
        setPreviewErr(errMsg(e));
      }
    }, 400);
    return () => clearTimeout(id);
  }, [symbol, side, slValue, refForPreview]);

  const place = async (forSide = side) => {
    if (!symbol) {
      toast.error("Pick an instrument first");
      return;
    }
    if (isLimit && !limitPrice) {
      toast.error("Order price is required for a limit order");
      return;
    }
    if (!slValue) {
      toast.error(
        isPoints ? "Enter the Stop Loss distance in points" : "Stop Loss is mandatory");
      return;
    }
    setBusy(true);
    try {
      const body = {
        symbol,
        side: forSide,
        stop_loss: slValue,
        tp1: tp1 ? parseFloat(tp1) : null,
        tp2: tp2 ? parseFloat(tp2) : null,
        move_be_on_tp1: moveBe,
      };
      let result;
      let message;
      if (isLimit) {
        const { data } = await api.post("/orders", { ...body, price: parseFloat(limitPrice) });
        result = data;
        message = `${data.kind.replace("_", " ").toUpperCase()} ${symbol} ${data.volume} lots @ ${data.price}`;
      } else {
        const { data } = await api.post("/trade/open", body);
        result = data;
        message = `${forSide.toUpperCase()} ${symbol} ${data.volume} lots @ ${data.entry}`;
      }
      toast.success(message);
      setSl("");
      setSlPoints("");
      setTp1("");
      setTp2("");
      setLimitPrice("");
      setPreview(null);
      return { ok: true, message, result };
    } catch (err) {
      const error = errMsg(err);
      toast.error(error);
      return { ok: false, error };
    } finally {
      setBusy(false);
    }
  };

  const placeDesktop = async () => {
    const r = await place();
    if (r?.ok) onPlaced?.(r.result);
  };

  /* Mobile: tapping Buy/Sell opens the confirmation sheet with a freshly priced summary. */
  const openSheet = async (forSide) => {
    if (!symbol) {
      toast.error("Pick an instrument first");
      return;
    }
    if (isLimit && !limitPrice) {
      toast.error("Order price is required for a limit order");
      return;
    }
    if (!slValue) {
      toast.error(
        isPoints ? "Enter the Stop Loss distance in points" : "Stop Loss is mandatory");
      return;
    }
    setSide(forSide);
    setSheetResult(null);
    let p = preview;
    try {
      const { data } = await api.post("/trade/preview", {
        symbol,
        side: forSide,
        stop_loss: slValue,
        ref_price: refForPreview,
      });
      p = data;
      setPreview(data);
    } catch (e) {
      if (!isLimit) {
        toast.error(errMsg(e));
        return;
      }
    }
    setSheet({
      symbol,
      side: forSide,
      kind: isLimit ? "Limit order" : "Market order",
      volume: p?.volume ?? "—",
      price: isLimit ? limitPrice : null,
      sl: slValue,
      tp1: tp1 || p?.auto_tp1 || "—",
      tp2: tp2 || p?.auto_tp2 || "—",
      trailing: false,
      trailPct: settings?.trail_pct ?? 25,
    });
  };

  const confirmSheet = async () => {
    const r = await place(sheet.side);
    if (!r) return;
    setSheetResult(r);
    if (r.ok) {
      setTimeout(() => {
        setSheet(null);
        setSheetResult(null);
        onPlaced?.(r.result);
      }, 900);
    }
  };

  const ready = Boolean(symbol && slValue && (isLimit ? limitPrice : true) && preview?.volume);
  const sideBtn = (active, tone) =>
    `rounded-sm py-3 text-sm font-bold uppercase tracking-[0.12em] border transition-colors duration-200 active:scale-95 ${
      active
        ? tone === "buy"
          ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
          : "bg-red-500/20 border-red-500 text-red-300"
        : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
    }`;

  return (
    <div
      data-testid="trade-form"
      className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-4 sm:p-5 flex flex-col gap-4"
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-heading font-semibold tracking-tight text-zinc-100">
          {isLimit ? "New Limit Order" : "New Market Order"}
        </h3>
        <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
          {settings?.risk_method === "fixed"
            ? `$${settings?.risk_fixed_amount} risk`
            : `${settings?.risk_percent}% risk`}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[
          ["market", "Market order", "order-type-market-btn"],
          ["limit", "Limit order", "order-type-limit-btn"],
        ].map(([v, lbl, tid]) => (
          <button
            key={v}
            type="button"
            data-testid={tid}
            onClick={() => setOrderType(v)}
            className={`rounded-sm py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] border transition-colors duration-200 ${
              orderType === v
                ? "border-emerald-600 bg-emerald-500/15 text-emerald-300"
                : "border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      <div>
        <Label>Instrument</Label>
        <InstrumentPicker symbols={symbols} value={symbol} onChange={setSymbol} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid="side-buy-btn"
          onClick={() => setSide("buy")}
          className={sideBtn(side === "buy", "buy")}
        >
          Buy
        </button>
        <button
          type="button"
          data-testid="side-sell-btn"
          onClick={() => setSide("sell")}
          className={sideBtn(side === "sell", "sell")}
        >
          Sell
        </button>
      </div>

      {isLimit && (
        <div>
          <Label>Order price · required</Label>
          <input
            data-testid="limit-price-input"
            className={inputCls}
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value)}
            placeholder="0.00000"
            inputMode="decimal"
          />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-400">
            Stop Loss · required
          </label>
          <div className="flex rounded-sm border border-zinc-700 overflow-hidden">
            {[
              ["price", "Price", "sl-mode-price-btn"],
              ["points", "Points", "sl-mode-points-btn"],
            ].map(([v, lbl, tid]) => (
              <button
                key={v}
                type="button"
                data-testid={tid}
                onClick={() => setSlMode(v)}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${
                  slMode === v
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
        {isPoints ? (
          <>
            <input
              data-testid="sl-points-input"
              className={inputCls}
              value={slPoints}
              onChange={(e) => setSlPoints(e.target.value)}
              placeholder="points from entry · e.g. 20"
              inputMode="decimal"
            />
            <p className="mt-1.5 font-mono text-[11px] text-zinc-400" data-testid="sl-points-hint">
              {pointsSl !== null ? (
                <>
                  {slPoints} pts {side === "buy" ? "below" : "above"}{" "}
                  {isLimit ? "the order price" : "the market"} →{" "}
                  <span className="text-amber-300" data-testid="sl-derived-price">
                    SL {pointsSl}
                  </span>
                </>
              ) : (
                `Waiting for ${isLimit ? "an order price" : "a live price"}`
              )}
            </p>
          </>
        ) : (
          <input
            data-testid="sl-input"
            className={inputCls}
            value={sl}
            onChange={(e) => setSl(e.target.value)}
            placeholder="0.00000"
            inputMode="decimal"
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>TP1 ({settings?.tp1_ratio ?? 50}%)</Label>
          <input
            data-testid="tp1-input"
            className={inputCls}
            value={tp1}
            onChange={(e) => setTp1(e.target.value)}
            placeholder={preview?.auto_tp1 ? `auto ${preview.auto_tp1}` : "optional"}
            inputMode="decimal"
          />
        </div>
        <div>
          <Label>TP2 (native)</Label>
          <input
            data-testid="tp2-input"
            className={inputCls}
            value={tp2}
            onChange={(e) => setTp2(e.target.value)}
            placeholder={preview?.auto_tp2 ? `auto ${preview.auto_tp2}` : "optional"}
            inputMode="decimal"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-sm border border-zinc-800 bg-black/40 px-3 py-2">
        <div>
          <Label>SL to break-even at TP1</Label>
          <p className="font-mono text-[11px] text-zinc-500">
            Desk rule: {settings?.move_be_on_tp1 === false ? "off" : "on"}
          </p>
        </div>
        <div className="flex gap-1">
          {[["rule", null], ["on", true], ["off", false]].map(([lbl, val]) => (
            <button
              key={lbl}
              type="button"
              data-testid={`move-be-${lbl}-btn`}
              onClick={() => setMoveBe(val)}
              className={`px-2.5 py-1.5 rounded-sm font-mono text-[11px] uppercase tracking-wide transition-colors duration-200 ${
                moveBe === val
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-600/60"
                  : "border border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {(settings?.tp1_r || settings?.tp2_r) && (
        <p className="text-[11px] font-mono text-zinc-400 -mt-2" data-testid="rr-hint">
          Blank TP auto-fills at TP1 {settings?.tp1_r || 0}R / TP2 {settings?.tp2_r || 0}R
          {preview?.auto_tp1 ? ` → ${preview.auto_tp1} / ${preview.auto_tp2 ?? "—"}` : ""}
        </p>
      )}

      <div
        className="border border-zinc-800 bg-black/50 rounded-sm p-3 font-mono text-xs space-y-1.5"
        data-testid="size-preview"
      >
        <div className="flex justify-between text-zinc-400">
          <span>{isLimit ? "Order price" : "Entry"}</span>
          <span className="text-zinc-100">{preview?.entry ?? refPrice ?? "—"}</span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Stop Loss</span>
          <span className="text-zinc-100" data-testid="preview-sl">
            {slValue ?? "—"}
          </span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>SL distance</span>
          <span className="text-zinc-100" data-testid="preview-distance">
            {preview?.distance ?? "—"}
          </span>
        </div>
        <div className="flex justify-between text-zinc-400">
          <span>Risk</span>
          <span className="text-zinc-100">
            {preview ? `$${preview.risk_amount.toFixed(2)}` : "—"}
          </span>
        </div>
        <div className="flex justify-between border-t border-zinc-800 pt-1.5">
          <span className="text-zinc-300">Lot size</span>
          <span className="text-emerald-300 text-sm" data-testid="calculated-volume">
            {preview?.volume ?? "—"}
          </span>
        </div>
        {previewErr && (
          <p className="text-red-300 text-[11px] leading-snug pt-1" data-testid="preview-error">
            {previewErr}
          </p>
        )}
      </div>

      {/* mobile: two-tap — Buy/Sell opens the confirmation sheet */}
      <div className="md:hidden grid grid-cols-2 gap-2">
        <button
          type="button"
          data-testid="mobile-buy-btn"
          disabled={busy}
          onClick={() => openSheet("buy")}
          className="h-12 rounded-full bg-emerald-600 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-200 active:scale-95 disabled:opacity-40"
        >
          Buy
        </button>
        <button
          type="button"
          data-testid="mobile-sell-btn"
          disabled={busy}
          onClick={() => openSheet("sell")}
          className="h-12 rounded-full bg-red-600 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-200 active:scale-95 disabled:opacity-40"
        >
          Sell
        </button>
        <p className="col-span-2 text-center font-mono text-[10px] text-zinc-400">
          You will be asked to confirm before anything is sent
        </p>
      </div>

      {/* desktop: plain button */}
      <button
        type="button"
        data-testid="submit-trade-btn"
        onClick={placeDesktop}
        disabled={busy || !ready}
        className={`hidden md:block w-full rounded-sm py-3 text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${
          side === "buy" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"
        }`}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
        ) : isLimit ? (
          `Place ${side} limit order`
        ) : (
          `Place ${side} market order`
        )}
      </button>

      <ConfirmSheet
        open={Boolean(sheet)}
        trade={sheet}
        busy={busy}
        result={sheetResult}
        onConfirm={confirmSheet}
        onClose={() => {
          setSheet(null);
          setSheetResult(null);
        }}
      />
    </div>
  );
};
